'use client';

import { useMemo, useState } from 'react';

import { Hero } from '@/components/Hero';
import { Methodology } from '@/components/Methodology';
import { StatCards } from '@/components/StatCards';
import { ChartControls } from '@/components/chart/ChartControls';
import { PizzaChart } from '@/components/chart/PizzaChart';
import { YearlyPriceTable } from '@/components/chart/YearlyPriceTable';
import { usePizzaSeries } from '@/components/chart/usePizzaSeries';
import { DEFAULT_METRIC, DEFAULT_SCALE } from '@/lib/constants';
import type { ChartScale, MetricId, PizzaModelId } from '@/lib/types';

export default function HomePage() {
  const [metric, setMetric] = useState<MetricId>((process.env.NEXT_PUBLIC_DEFAULT_METRIC as MetricId) || (DEFAULT_METRIC as MetricId));
  const [scale, setScale] = useState<ChartScale>((process.env.NEXT_PUBLIC_DEFAULT_SCALE as ChartScale) || (DEFAULT_SCALE as ChartScale));
  const [pizzaModel, setPizzaModel] = useState<PizzaModelId>('yearly_schedule_usd');

  const { data, error, isLoading } = usePizzaSeries(metric, pizzaModel);

  const latestPoint = useMemo(() => {
    if (!data?.points?.length) return null;
    return data.points[data.points.length - 1];
  }, [data]);

  return (
    <>
      <Hero />

      <section id="chart" className="section">
        <div className="section-head">
          <h2>Interactive Pizza Power Chart</h2>
          <p className="muted">Default view: pizzas per BTC (linear). You can switch metric, scale, and pricing model anytime.</p>
        </div>

        <ChartControls
          metric={metric}
          onMetricChange={setMetric}
          scale={scale}
          onScaleChange={setScale}
          pizzaModel={pizzaModel}
          onPizzaModelChange={setPizzaModel}
        />

        <div className="chart-layout">
          <div className="chart-card">
            <PizzaChart
              points={data?.points ?? []}
              metricLabel={data?.meta.metricLabel ?? 'Pizzas per 1 BTC'}
              scale={scale}
              isLoading={isLoading}
              error={error}
            />
          </div>
          <YearlyPriceTable />
        </div>

        <StatCards latestPoint={latestPoint} />
      </section>

      <Methodology meta={data?.meta} pizzaModel={pizzaModel} />
    </>
  );
}
